import {
	AIAPIMethods,
	AICallbackMethods,
	IAsset, 
	IMessage,
	IObjectInfo,
	MethodDescriptor,
	OpenAICompletionRating,
	UpdateType
} from "common";

import {ExecutableFunction} from "./functions/ExecutableFunction";
import { WebSocketConnectionManager, WEB_SOCKET_DISCONNECT_EVENT, WEB_SOCKET_CONNECT_EVENT } from './WebSocketConnectionManager';
import store from "../../state/store";
import {MessageModelWithGuid} from '../../interfaces/Misc';

export { WEB_SOCKET_DISCONNECT_EVENT, WEB_SOCKET_CONNECT_EVENT };

type CallbackFunction = (type: UpdateType, message: string, guid: string, total: number, completed: number, sessionId: string) => void;

export default class AIAPIUtility
{
	static _secondaryPrompts: Array<ExecutableFunction>;
	static webSocketConnectionManager = WebSocketConnectionManager.getInstance();

	/**
     * Sends a prompt to the AI server for processing
     * @param prompt - The text prompt to send to the AI
     * @param userid - The ID of the user sending the prompt
     * @param messageGuid - Optional unique identifier for the message, used for tracking and updating
     * @param sessionId - The ID of the current chat session
     * @param secondaryPrompts - Array of executable functions that can be called by the AI
     * @param attachments - Optional array of assets to include with the prompt
     * @param displayUnitScale - The unit scale to use for display (e.g., "meters")
     * @param systemPrompt - The system-level instructions or context provided to guide the AI's responses
	 * @returns A Promise resolving to the AI's response
     */
	static async prompt(prompt: string,
		userid: string,
		messageGuid: string | null | undefined,
		sessionId: string,
		secondaryPrompts: Array<ExecutableFunction>,
		attachments: string[] | null | undefined,
		displayUnitScale: string,
		systemPrompt: string)
	{
		const secondaryPromptFunctions: { [key: string]: MethodDescriptor } = {};
		secondaryPrompts.forEach(value =>
		{
			secondaryPromptFunctions[value.name] = value.descriptor;
		})

		messageGuid = messageGuid || "";

		const attachmentList = attachments && attachments.length > 0 ? attachments : [];

		prompt = prompt.replace("\\", "\\\\");

		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("prompt", {
			prompt,
			userid,
			messageGuid,
			sessionId,
			secondaryPromptFunctions,
			attachments: attachmentList,
			displayUnitScale
			, systemPrompt });
	}

	static registerForUpdate(callback: CallbackFunction)
	{
		function cb(type: UpdateType, message: string, guid: string, total: number, completed: number, sessionId: string)
		{
			callback(type, message, guid, total, completed, sessionId);
		}

		AIAPIUtility.webSocketConnectionManager.callbacks.push(cb);
		return cb; // Return the wrapped callback so it can be unregistered
	}

	static unregisterForUpdate(callback: CallbackFunction)
	{
		const idx = AIAPIUtility.webSocketConnectionManager.callbacks.indexOf(callback);
		if (idx !== -1)
		{
			AIAPIUtility.webSocketConnectionManager.callbacks.splice(idx, 1);
		}
	}

	static registerLocalAPIFunctions(secondaryPrompts: Array<ExecutableFunction>)
	{
		AIAPIUtility._secondaryPrompts = secondaryPrompts;
		AIAPIUtility.webSocketConnectionManager.setSecondaryPrompts(secondaryPrompts);
	}

	static async cancel(guid: string)
	{
		await AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("cancel", guid);
	}

	static async translate(text: string, targetLanguage: string)
	{
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("translate", {text, targetLanguage});
	}

	static storeNewPromptCompletion = async (id: string, rawPrompt: string, prompt: string, rating: OpenAICompletionRating, completion: string, reason: string, version: string) =>
	{
		const userId = store.getState().userModule.sessionInfo!.userId;
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient
			.request("storeNewPromptCompletion", {id, rawPrompt, completion, prompt, rating, reason, userId, version})
	};

	static listPromptCompletions = async () =>
	{
		const userId = store.getState().userModule.sessionInfo!.userId;
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient
			.request("listPromptCompletions", {userId});
	}

	static listGoodPromptCompletions = async () =>
	{
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient
			.request("listGoodPromptCompletions", {});
	}

	static async deletePromptCompletion(id: string)
	{
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("deletePromptCompletion", {id});
	}

	static initialize()
	{
		AIAPIUtility.webSocketConnectionManager.establishConnection(true); // Initial connection needs cleanup
	}

	/**
	 * Reset the WebSocket connection - useful for recovery from stuck states
	 */
	static async resetConnection()
	{
		await AIAPIUtility.webSocketConnectionManager.resetConnection();
	}

	/**
	 * Get the current WebSocket connection status
	 */
	static getConnectionStatus()
	{
		return AIAPIUtility.webSocketConnectionManager.getConnectionStatus();
	}

	static guessTheObjectName(objectInfo: IObjectInfo[], parents: IObjectInfo[], parentImageUrls: string[], imageUrls: string[], vocabulary: string[] | null)
	{
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("guessTheObjectName", {
			objectInfo,
			parents,
			parentImageUrls,
			imageUrls,
			vocabulary
		});
	}

	static updateSessionName(sessionId: string, sessionName: string)
	{
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("updateSessionName", {
			sessionId,
			sessionName
		});
	}

	static updateSessionActive(sessionId: string)
	{
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("updateSessionActive", {
			sessionId
		});
	}

	static persistMessages(userid: string, AISessionGuid: string, updatedMessages: MessageModelWithGuid[])
	{
		const projectId = store.getState().scenarioModule.scenarioId ?? undefined;
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("persistMessages", {
			userid,
			AISessionGuid,
			updatedMessages,
			projectId
		});
	}

	static async deleteMessages(sessionId: string, messageIds: string[]): Promise<{ success: boolean, deletedCount: number, error?: string }>
	{
		return await AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("deleteMessages", {
			sessionId,
			messageIds
		});
	}

	static retrieveSessions(userid: string, projectId: string)
	{
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("retrieveSessions", {
			userid,
			projectId
		});
	}

	static retrieveMessages(userid: string, sessionId: string)
	{
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("retrieveMessages", {
			userid,
			AISessionGuid: sessionId
		});
	}

	/**
	 * If an execution error occurs, send it to the server to iterate on it
	 * @param error
	 * @param sessionId
	 * @param secondaryPromptFunctions
	 * @param displayUnitScale
	 * @param systemPrompt
	 */
	static handleCodeExecutionError(error: Error, sessionId: string, secondaryPromptFunctions: { [p: string]: MethodDescriptor }, displayUnitScale: string, systemPrompt: string)
	{
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("handleCodeExecutionError", {
			message: error.message,
			sessionId: sessionId,
			secondaryPromptFunctions, // Passing these values as parameters instead of hardcoded empty objects.
			displayUnitScale,
			systemPrompt
		});
	}

	/**
	 * After a piece of code executes, send it to the server to finalize the command. The return value is a summary
	 * suitable as chat message
	 * @param sessionId
	 */
	static finalizeCode(sessionId: string)
	{
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("finalizeCode", {
			sessionId,
		});
	}

	static deleteSession(sessionId: string)
	{
		return AIAPIUtility.webSocketConnectionManager.jsonRpcClient.request("deleteSession", {
			sessionId,
		});
	}
}